import * as React from 'react'
import { cn } from '@/lib/utils'

type PopoverContextValue = {
  open: boolean
  setOpen: (open: boolean) => void
  triggerRef: React.RefObject<HTMLElement | null>
  contentRef: React.RefObject<HTMLDivElement | null>
}

const PopoverContext = React.createContext<PopoverContextValue | null>(null)

function usePopoverContext() {
  const context = React.useContext(PopoverContext)

  if (!context) {
    throw new Error('Popover components must be used within Popover')
  }

  return context
}

function composeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(node)
      } else if (ref && 'current' in ref) {
        ;(ref as React.MutableRefObject<T>).current = node
      }
    })
  }
}

interface PopoverProps {
  children: React.ReactNode
  className?: string
}

function Popover({ children, className }: PopoverProps) {
  const [open, setOpen] = React.useState(false)
  const triggerRef = React.useRef<HTMLElement | null>(null)
  const contentRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (!open) return

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node
      if (
        triggerRef.current?.contains(target) ||
        contentRef.current?.contains(target)
      ) {
        return
      }

      setOpen(false)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  return (
    <PopoverContext.Provider value={{ open, setOpen, triggerRef, contentRef }}>
      <div className={cn('relative inline-flex', className)}>{children}</div>
    </PopoverContext.Provider>
  )
}

interface PopoverTriggerProps {
  asChild?: boolean
  children: React.ReactElement
}

const PopoverTrigger = React.forwardRef<HTMLElement, PopoverTriggerProps>(
  ({ asChild = false, children }, ref) => {
    const { open, setOpen, triggerRef } = usePopoverContext()
    const child = React.Children.only(children) as React.ReactElement<{
      onClick?: (event: React.MouseEvent<HTMLElement>) => void
    }>
    const childRef = (child as React.ReactElement & { ref?: React.Ref<HTMLElement> }).ref

    if (!asChild) {
      return React.cloneElement(
        <button type="button" />,
        {
          ref: composeRefs(triggerRef, ref),
          onClick: () => setOpen(!open),
        },
        child
      )
    }

    return React.cloneElement(child as React.ReactElement<Record<string, unknown>>, {
      ref: composeRefs(triggerRef, ref, childRef),
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        child.props.onClick?.(event)
        if (!event.defaultPrevented) {
          setOpen(!open)
        }
      },
      'aria-expanded': open,
      'data-state': open ? 'open' : 'closed',
    })
  }
)
PopoverTrigger.displayName = 'PopoverTrigger'

interface PopoverContentProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: 'start' | 'center' | 'end'
}

const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  ({ className, align = 'center', ...props }, ref) => {
    const { open, contentRef } = usePopoverContext()

    if (!open) return null

    return (
      <div
        ref={composeRefs(contentRef, ref)}
        className={cn(
          'absolute top-full z-50 mt-2 w-72 rounded-md border bg-popover text-popover-foreground shadow-md outline-none',
          align === 'start' && 'left-0',
          align === 'center' && 'left-1/2 -translate-x-1/2',
          align === 'end' && 'right-0',
          className
        )}
        {...props}
      />
    )
  }
)
PopoverContent.displayName = 'PopoverContent'

export { Popover, PopoverTrigger, PopoverContent, usePopoverContext }
